/**
 * LA CACHÉ DE CONSULTAS, GUARDADA EN EL TELÉFONO (React Query).
 *
 * Lo que hace por pantalla: al abrirla, si en el teléfono hay una copia de su
 * consulta, se pinta AL INSTANTE con esa copia y, si está vieja, se pide la
 * buena por detrás; cuando llega, se cambia sola. Es el «abro WhatsApp y ya
 * están mis chats» aplicado a cada lista de la app.
 *
 * Es la idea de `experimental_createQueryPersister` de TanStack (guardar consulta
 * a consulta, no toda la caché de golpe —con años de historia, serializar todo en
 * cada cambio colgaría el móvil—), escrita aquí para tres cosas que la de la
 * biblioteca no hace:
 *
 *   · POR USUARIO: la copia de uno nunca se le enseña a otro (`usuarioOffline`).
 *   · SIN CADUCIDAD: una copia de hace meses sigue valiendo sin cobertura; la
 *     frescura la decide `staleTime`, no la edad de la copia.
 *   · DE OTRA VERSIÓN DE LA APP: con red se pide la buena primero (la forma de
 *     los datos pudo cambiar con la versión); solo sin red se enseña la vieja.
 *
 * Y para guardar no envuelve la consulta: escucha la caché (`vigilarCache`), así
 * que también se guarda lo que cambia por `setQueryData` (altas optimistas,
 * tiempo real), no solo lo que llega de una consulta.
 *
 * Funciona junto con `networkMode: "offlineFirst"` (App.tsx): con el modo por
 * defecto, sin red la consulta ni siquiera arranca y la copia nunca se leería.
 */
import type { Query, QueryClient, QueryFunctionContext, QueryKey, QueryPersister } from "@tanstack/react-query";
import { guardar, leer } from "./almacenOffline";
import { modoActivo, sinRedSegura, usuarioOffline } from "./estadoOffline";

/**
 * Versión del bundle. Cambia con cada despliegue (Vite deja el nombre del
 * fichero con su huella); sirve para saber si una copia la escribió esta misma
 * versión de la app.
 */
export const VERSION_APP: string = (() => {
  try {
    const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
    return s?.src.split("/").pop() ?? "dev";
  } catch {
    return "dev";
  }
})();

export interface ConsultaGuardada {
  queryKey: QueryKey;
  data: unknown;
  dataUpdatedAt: number;
  version: string;
}

/**
 * Consultas que NO se guardan:
 *   · `meta: { offline: false }` en la propia consulta;
 *   · las que llevan URLs firmadas (caducan en minutos y no valen de nada luego).
 */
const PREFIJOS_QUE_NO_SE_GUARDAN = ["signed-url", "signedUrl", "invoice-file-url", "pdf-signed"];

export function seGuarda(query: Pick<Query, "queryKey" | "meta">): boolean {
  if ((query.meta as { offline?: boolean } | undefined)?.offline === false) return false;
  const primero = Array.isArray(query.queryKey) ? String(query.queryKey[0] ?? "") : "";
  return !PREFIJOS_QUE_NO_SE_GUARDAN.some((p) => primero.includes(p));
}

export async function leerConsulta(uid: string, queryHash: string): Promise<ConsultaGuardada | null> {
  const c = await leer<ConsultaGuardada>("consultas", uid, queryHash);
  return c && c.dataUpdatedAt ? c : null;
}

/**
 * El `persister` de cada consulta (se pone en `defaultOptions.queries`).
 * React Query lo llama EN LUGAR de la consulta: decide si contesta la copia o la red.
 */
export const persistidorOffline = (async (
  queryFn: (ctx: QueryFunctionContext<QueryKey>) => unknown,
  ctx: QueryFunctionContext<QueryKey>,
  query: Query,
) => {
  const uid = usuarioOffline();
  if (!modoActivo() || !uid || !seGuarda(query) || query.state.data !== undefined) return queryFn(ctx);

  const copia = await leerConsulta(uid, query.queryHash);
  if (!copia) return queryFn(ctx);

  /* Copia de OTRA versión y con red: primero la red (la forma pudo cambiar);
     si la red no contesta, la copia. */
  if (copia.version !== VERSION_APP && !sinRedSegura()) {
    try {
      return await queryFn(ctx);
    } catch (e) {
      if (ctx.signal?.aborted) throw e;
      return copia.data;
    }
  }

  /* Copia buena: se contesta con ella YA. Justo después se le pone su hora real
     (si no, React Query la daría por recién llegada) y, si está vieja, se pide
     la buena por detrás. Igual que la biblioteca de TanStack. */
  if (copia.data !== null && typeof copia.data === "object") restaurados.add(copia.data as object);
  setTimeout(() => {
    query.setState({ dataUpdatedAt: copia.dataUpdatedAt });
    if (query.isStale() && !sinRedSegura()) void query.fetch().catch(() => undefined);
  }, 0);
  return copia.data;
}) as unknown as QueryPersister;

/* ─── Guardar lo que cambia ──────────────────────────────────────────────── */

const pendientes = new Map<string, ReturnType<typeof setTimeout>>();
const RETARDO_GUARDADO_MS = 800;
/** Datos que acaban de salir del teléfono: volver a escribirlos no aporta nada. */
const restaurados = new WeakSet<object>();

function programarGuardado(query: Query): void {
  const uid = usuarioOffline();
  if (!modoActivo() || !uid || !seGuarda(query)) return;
  const d = query.state.data;
  if (d !== null && typeof d === "object" && restaurados.has(d as object)) return;
  const hashConsulta = query.queryHash;
  const previo = pendientes.get(hashConsulta);
  if (previo) clearTimeout(previo);
  pendientes.set(
    hashConsulta,
    setTimeout(() => {
      pendientes.delete(hashConsulta);
      // El usuario pudo cambiar en estos 800 ms (cerrar sesión): no se guarda nada a nadie.
      if (usuarioOffline() !== uid) return;
      const { data, dataUpdatedAt } = query.state;
      if (data === undefined || !dataUpdatedAt) return;
      const valor: ConsultaGuardada = { queryKey: query.queryKey, data, dataUpdatedAt, version: VERSION_APP };
      void guardar("consultas", uid, hashConsulta, valor);
    }, RETARDO_GUARDADO_MS),
  );
}

/**
 * Escucha la caché de React Query y guarda cada consulta cuando le llegan datos
 * (de la red o de `setQueryData`). Devuelve la función para dejar de escuchar.
 */
export function vigilarCache(queryClient: QueryClient): () => void {
  /* Solo se escucha «llegaron datos». Que React Query suelte una consulta de la
     memoria (gcTime, o `removeQueries` al cambiar de empresa) NO borra su copia:
     la copia es justo para cuando la memoria ya no la tiene, y sus claves llevan
     la empresa, así que la de otra empresa nunca se confunde. */
  return queryClient.getQueryCache().subscribe((evento) => {
    if (evento.type === "updated" && evento.action.type === "success") programarGuardado(evento.query);
  });
}

/** Al cerrar sesión: que no quede ningún guardado en vuelo con el usuario que se va. */
export function cancelarGuardadosPendientes(): void {
  pendientes.forEach((t) => clearTimeout(t));
  pendientes.clear();
}
